import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  PdfPen,
  PdfColor,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { OptsImage } from '../optsImage';
import { IItemCount } from '@educraft/base';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';
import '../../pdf/draw/transforms';

interface HodinyConfig {
  imageCount: number;
  numberIndices: Set<number>;
}

const GRID_CONFIGS: HodinyConfig[] = [
  { imageCount: 12, numberIndices: new Set() },
  { imageCount: 10, numberIndices: new Set([0, 6]) },
  { imageCount: 6, numberIndices: new Set([1, 3, 5, 7, 9, 11]) },
  { imageCount: 4, numberIndices: new Set([1, 2, 4, 5, 7, 8, 10, 11]) },
  { imageCount: 2, numberIndices: new Set([1, 2, 3, 4, 5, 7, 8, 9, 10, 11]) },
];

function getConfig(imageCount: number): HodinyConfig {
  for (const config of GRID_CONFIGS) {
    if (imageCount >= config.imageCount) {
      return config;
    }
  }
  return GRID_CONFIGS[GRID_CONFIGS.length - 1];
}

const NUMBER_STRINGS = ['12', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'];

/**
 * Hodiny (Clock) - Clock face with configurable image positions
 *
 * Layout: Portrait page with large clock face.
 * Image positions are determined by opts.list.length:
 *   2 images → images at positions {12, 6}, numbers elsewhere
 *   4 images → images at positions {12, 3, 6, 9}, numbers elsewhere
 *   6 images → images at positions {12, 2, 4, 6, 8, 10}, numbers elsewhere
 *   10 images → images at {1,2,3,4,5,7,8,9,10,11}, numbers at {12, 6} (original layout)
 *   12 images → all positions are images
 *
 * Migrated from C# EduFrame.Img10.Generators.Hodiny
 */
export class Hodiny implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 12;
  }

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const page = pdf.addPage();

    const margin = PdfUnit.FromMillimeters(5);
    const sz = PdfSize.A4.width - 2 * margin;
    const originPt = new PdfPoint(margin, (PdfSize.A4.height - sz) / 2);
    const imageSize = PdfUnit.FromMillimeters(30);
    const font = new PdfFont(PdfFont.FONT_NAME, 72);

    const rect = new PdfRect(originPt.x, originPt.y, sz, sz);
    const cx = rect.x + rect.width / 2;
    const cy = rect.y + rect.height / 2;
    const center = new PdfPoint(cx, cy);

    page.drawEllipse(GUtil.basePen, null, rect);
    page.cutCircle(center);

    const pen2 = new PdfPen(PdfColor.fromRgb(0, 0, 0), 3);

    for (let i = 0; i < 60; i++) {
      page.save();
      page.rotateAtTransform(i * 6, center);

      const p1 = new PdfPoint(cx, rect.y);
      const p2 = new PdfPoint(cx, rect.y + PdfUnit.FromMillimeters(7));

      if (i % 5 === 0) {
        page.drawLine(pen2, p1, p2);
      } else {
        page.drawLine(GUtil.basePen, p1, p2);
      }

      page.restore();
    }

    const count = Math.max(2, Math.min(opts.list.length, 12));
    const config = getConfig(count);
    let imageIndex = 0;

    for (let i = 0; i < 12; i++) {
      const angle = (Math.PI / 6) * i;
      const radius = sz / 2 - imageSize * 0.7;
      const x = cx + Math.sin(angle) * radius;
      const y = cy - Math.cos(angle) * radius;
      const r = new PdfRect(x - imageSize / 2, y - imageSize / 2, imageSize, imageSize);

      if (config.numberIndices.has(i)) {
        page.drawString(NUMBER_STRINGS[i], font, PdfBrush.Black, null, r, ETextAlign.Center);
      } else {
        if (imageIndex < opts.list.length) {
          page.drawImageCenter(opts.list[imageIndex].image, r);
        }
        imageIndex++;
      }
    }
  }
}
